import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppDispatch } from 'hooks';
import { Button } from 'reactstrap';

import { duplicateField, removeField, selectField, updateField } from '~forms/reducers/formBuilderSlice';
import { FormField, MergeFieldRegistryEntry, RichTextFormField } from '~forms/types';
import { InlineTokenText } from './MergeFieldToken';
import { RichTextEditor, RichTextView } from './rich-text';

const FIELD_TYPE_LABELS: Record<string, string> = {
  single_line_text: 'Single Line',
  paragraph_text: 'Paragraph',
  date: 'Date',
  checkbox: 'Checkbox',
  multiple_choice: 'Multiple Choice',
  dropdown: 'Dropdown',
  signature: 'Signature',
  section_divider: 'Section',
  merge_field: 'Merge Field',
  rich_text: 'Rich Text',
};

interface ComponentProps {
  field: FormField;
  isSelected: boolean;
  readOnly: boolean;
  registry: MergeFieldRegistryEntry[];
}

const FieldCard: React.FC<ComponentProps> = ({ field, isSelected, readOnly, registry }) => {
  const dispatch = useAppDispatch();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSelect = () => {
    if (!readOnly) dispatch(selectField(field.id));
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(duplicateField(field.id));
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(removeField(field.id));
  };

  // Canvas fields are directly editable — typing here sets the field's default/prefill value.
  const handleValueChange = (default_value: string) => {
    if (!readOnly) dispatch(updateField({ id: field.id, default_value }));
  };

  // Rich-text blocks store their formatted HTML in `content`, edited inline in the card.
  const handleRichTextChange = (content: string) => {
    if (!readOnly) dispatch(updateField({ id: field.id, content }));
  };

  const dv = field.default_value ?? '';
  const tokenPreview = dv.includes('{{merge:') ? (
    <div className="field-preview-token-preview">
      <small className="text-muted me-1">Prefilled:</small>
      <InlineTokenText text={dv} registry={registry} />
    </div>
  ) : null;

  const renderPreview = () => {
    switch (field.type) {
      case 'single_line_text':
        return (
          <div>
            {field.help_text && <small className="d-block text-muted mb-1">{field.help_text}</small>}
            <input
              className="field-preview-input"
              value={dv}
              placeholder="Default / prefill value"
              readOnly={readOnly}
              onChange={(e) => handleValueChange(e.target.value)}
              aria-label="Default value"
            />
            {tokenPreview}
          </div>
        );
      case 'paragraph_text':
        return (
          <div>
            {field.help_text && <small className="d-block text-muted mb-1">{field.help_text}</small>}
            <textarea
              className="field-preview-textarea"
              value={dv}
              placeholder="Default / prefill text"
              readOnly={readOnly}
              onChange={(e) => handleValueChange(e.target.value)}
              aria-label="Default value"
            />
            {tokenPreview}
          </div>
        );
      case 'date': {
        // Default is configured from the right panel (Today / Yesterday / Custom). Canvas shows a summary.
        const dateLabels: Record<string, string> = { today: "Today's date", yesterday: "Yesterday's date" };
        const dateLabel = dateLabels[dv] || dv || 'No default';
        return (
          <div>
            {field.help_text && <small className="d-block text-muted mb-1">{field.help_text}</small>}
            <input className="field-preview-input" type="text" value={dateLabel} readOnly aria-label="Default date" />
          </div>
        );
      }
      case 'checkbox':
        return (
          <div className="field-preview-checkbox">
            <input
              type="checkbox"
              checked={dv === 'true'}
              disabled={readOnly}
              onChange={(e) => handleValueChange(e.target.checked ? 'true' : 'false')}
              aria-label="Checked by default"
            />
            <span>{field.help_text || field.label || 'Checkbox'}</span>
          </div>
        );
      case 'multiple_choice':
      case 'dropdown': {
        const choiceField = field as Extract<FormField, { type: 'multiple_choice' | 'dropdown' }>;
        const opts = 'options' in choiceField ? choiceField.options : [];
        if (opts.length === 0) {
          return <span className="text-muted field-preview-empty">No options yet</span>;
        }
        if (field.type === 'dropdown') {
          return (
            <select
              className="field-preview-input"
              value={dv}
              disabled={readOnly}
              onChange={(e) => handleValueChange(e.target.value)}
              aria-label="Default selection"
            >
              <option value="">— No default —</option>
              {opts.map((opt) => (
                <option key={opt.id} value={opt.value || opt.label}>
                  {opt.label}
                </option>
              ))}
            </select>
          );
        }
        return (
          <div className="field-preview-choice">
            {opts.map((opt) => (
              <div key={opt.id} className="field-preview-choice-item">
                <input
                  type="radio"
                  name={`mc-${field.id}`}
                  checked={dv === (opt.value || opt.label)}
                  disabled={readOnly}
                  onChange={() => handleValueChange(opt.value || opt.label)}
                />
                <span>{opt.label}</span>
              </div>
            ))}
          </div>
        );
      }
      case 'signature':
        return <div className="field-preview-signature" aria-hidden="true">Signature area</div>;
      case 'section_divider':
        return <div className="field-preview-section" aria-hidden="true">{field.label || 'Section heading'}</div>;
      case 'merge_field': {
        const mf = field as Extract<FormField, { type: 'merge_field' }>;
        const entry = 'merge_token' in mf ? registry.find((r) => r.token === mf.merge_token) : undefined;
        return (
          <div className="field-preview-merge" aria-hidden="true">
            <span>{'{'}</span>
            <span>{entry?.label ?? ('merge_token' in mf ? mf.merge_token : '')}</span>
            <span>{'}'}</span>
          </div>
        );
      }
      case 'rich_text': {
        const rt = field as RichTextFormField;
        // Published/read-only versions show the rendered content; the open draft edits it inline.
        if (readOnly) {
          return <RichTextView html={rt.content} registry={registry} emptyText="No content" />;
        }
        return <RichTextEditor value={rt.content} onChange={handleRichTextChange} ariaLabel="Rich text content" />;
      }
      default:
        return null;
    }
  };

  const labelSegments = field.label ? (
    <InlineTokenText text={field.label} registry={registry} />
  ) : (
    <span className="text-muted">{FIELD_TYPE_LABELS[field.type] ?? field.type}</span>
  );

  const cardClasses = [
    'field-card',
    isSelected ? 'field-card--selected' : '',
    readOnly ? 'field-card--readonly' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={setNodeRef} style={style} className={cardClasses} onClick={handleSelect} data-cy={`field-card-${field.id}`}>
      <div className="field-card-header">
        {!readOnly && (
          <span
            className="field-card-drag-handle"
            {...attributes}
            {...listeners}
            title="Drag to reorder"
            aria-label="Drag to reorder field"
          >
            ⠿
          </span>
        )}
        <span className="field-card-type-badge">{FIELD_TYPE_LABELS[field.type] ?? field.type}</span>
        {!readOnly && isSelected && (
          <div className="field-card-actions">
            <Button
              size="sm"
              color="link"
              className="p-0 text-secondary"
              onClick={handleDuplicate}
              title="Duplicate"
              aria-label="Duplicate field"
            >
              ⧉
            </Button>
            <Button
              size="sm"
              color="link"
              className="p-0 text-danger"
              onClick={handleDelete}
              title="Delete"
              aria-label="Delete field"
            >
              ✕
            </Button>
          </div>
        )}
      </div>
      <div className="field-card-body">
        {/* section_divider and rich_text are content blocks — they have no field label row. */}
        {field.type !== 'section_divider' && field.type !== 'rich_text' && (
          <div className="field-card-label">
            {labelSegments}
            {field.required && <span className="text-danger ms-1" aria-hidden="true">*</span>}
          </div>
        )}
        <div className="field-card-preview">
          {renderPreview()}
        </div>
      </div>
    </div>
  );
};

export default FieldCard;
